import React, { useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';

import Button from '@/tayara-kit/Button';
import CriteriaSelect from '@/tayara-kit/CriteriaSelect';
import Text from '@/tayara-kit/Text';
import CriteriaTextarea from '@/tayara-kit/Textarea';

type ReportDialogProps = {
  isOpen: boolean; // Add isOpen prop to control visibility
  onClickSend: (reportType: string, reportMessage: string) => void;
  onCLickClose: () => void;
};

type ReportTypeItem = { label: string; value: string };

const listReportType: ReportTypeItem[] = [
  { label: 'Inapproprié', value: 'Inapproprié' },
  { label: 'Doublon', value: 'Doublon' },
  { label: 'Fraude', value: 'Fraude' },
  { label: 'Mauvaise catégorie', value: 'Mauvaise' },
  { label: 'Nudité', value: 'Nudité' },
  { label: 'Article déjà vendu', value: 'Vendu' },
];

function ReportDialog({
  isOpen,
  onClickSend,
  onCLickClose,
}: ReportDialogProps) {
  const [reportType, setReportType] = useState<string>('');
  const [reportMessage, setReportMessage] = useState<string>('');

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCLickClose}>
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur-md" />
        </Transition.Child>

        {/* Dialog Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl border border-secondary/50 bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Close Button */}
                <div
                  className="btn btn-sm btn-circle absolute right-2 top-2"
                  onClick={onCLickClose}
                >
                  ✕
                </div>

                {/* Dialog Content */}
                <Dialog.Title
                  as="h3"
                  className="w-3/4 lg:w-full font-semibold text-gray-800 mb-5"
                >
                  Quel est le problème avec cette annonce?
                </Dialog.Title>

                {/* Report Type Selection */}
                <div className="flex flex-col mb-2">
                  <Text
                    as="div"
                    text="Sélectionner le type d’abus"
                    className="text-xs font-medium text-gray-700 mb-2"
                  />
                  <CriteriaSelect
                    value={reportType}
                    onChange={(e) => {
                      if (e.target.value) {
                        setReportType(e.target.value as string);
                      }
                    }}
                    displayLabel="type d'abus"
                    options={listReportType}
                  />
                </div>

                {/* Report Message */}
                <div className="flex flex-col">
                  <Text
                    as="div"
                    text="Message"
                    className="text-xs font-medium text-gray-700 mb-2"
                  />
                  <CriteriaTextarea
                    value={reportMessage}
                    onChange={(e) => setReportMessage(e.target.value)}
                    displayLabel="Décrire en détail l'abus"
                  />
                </div>
                <div className="w-full flex justify-end items-center gap-2">
                  {/* Confirm Button */}
                  <Button
                    aria-label="Confirmer"
                    className="w-fit self-center text-white font-medium bg-red-500 hover:bg-primary my-2 lg:my-4 rounded"
                    onClick={() => onClickSend(reportType, reportMessage)}
                  >
                    Confirmer
                  </Button>
                  <Button
                    aria-label="Annuler"
                    className="w-fit self-center text-white font-medium bg-neutral-500 hover:bg-neutral-400 my-2 lg:my-4 rounded"
                    onClick={onCLickClose}
                  >
                    Annuler
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ReportDialog;
